import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Spinner } from "react-bootstrap";
import { ordersActions } from "../../../_store/order.slice";
import { ExternalWeighingTicketTable } from "./ExternalWeighingTicketTable";
import { DomainButton } from "../../Abstractions/DomainButton";
import { SelectTable } from "../../Abstractions/SelectTable";
import { Searcher } from "../../Forms/Searcher";
import { BadgeDomain } from "../../Abstractions/BadgeDomain";
import { BasicSwitch } from "../../Abstractions/BasicSwitch";
import { matchTicketsToExchanges } from "./matchTicketsToExchanges";
import { useToast } from "../../../_helpers/notifications/useToasts";

export function ExternalWeighingTicketSelect({ onTicketsSelected, wasteExchanges, externalContractorId }) {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterByContractor, setFilterByContractor] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState("");
  const { showErrorMessage } = useToast();

  const searchTickets = async (searchQuery, filterByContractorValue) => {
    setItems({ loading: true });

    const query = {
      search: searchQuery,
      externalContractorIds: filterByContractorValue && externalContractorId ? [externalContractorId] : []
    };

    const { payload, error } = await dispatch(ordersActions.searchExternalWeighingTickets({ query }));
    if (error) {
      showErrorMessage(error.message);
    }

    setItems(payload ?? []);
  };

  const onSearchChange = async ({ value }) => {
    setLastSearchQuery(value ?? "");
    await searchTickets(value, filterByContractor);
  };

  const handleFilterByContractorChange = async (checked) => {
    setFilterByContractor(checked);
    await searchTickets(lastSearchQuery, checked);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const externalTicketIds = selectedItems.map((item) => item.externalTicketId);
      const { payload: details } = await dispatch(
        ordersActions.getExternalWeighingTicketDetailsBatch({ externalTicketIds })
      );

      const tickets = (details ?? []).map((detail) => ({
        externalTicketId: detail.externalTicketId,
        ticketId: null,
        ticketNumber: detail.ticketNumber,
        netMass: (detail.weightNet / 1000).toFixed(4),
        remarks: detail.remarks ?? "",
        externalData: {
          dateWeighFirst: detail.dateWeighFirst,
          dateWeighSecond: detail.dateWeighSecond,
          weightFirst: detail.weightFirst,
          weightSecond: detail.weightSecond,
          weightNet: detail.weightNet,
          contractorName: detail.contractorName,
          driverName: detail.driverName,
          vehicleRegistrationNumber: detail.vehicleRegistrationNumber,
          wastes: detail.wastes
        },
        wastes: [],
        rejectedWastes: []
      }));

      const matchedTickets = matchTicketsToExchanges(tickets, wasteExchanges);
      onTicketsSelected(matchedTickets);
      setShow(false);
      setSelectedItems([]);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSetShow = (value) => {
    setShow(value);
    if (!value) {
      setSelectedItems([]);
      setItems([]);
    }
  };

  const selectContent = (
    <div className="d-flex align-items-center gap-3 px-3 pb-2">
      {selectedItems.length > 0 && (
        <BadgeDomain>Wybrano: {selectedItems.length}</BadgeDomain>
      )}
      <DomainButton
        onClick={handleSubmit}
        disabled={loading || selectedItems.length === 0}
        className="ms-auto"
      >
        {loading ? <><Spinner size="sm" /> Pobieranie...</> : "Zatwierdź wybrane kwity"}
      </DomainButton>
    </div>
  );

  const searcher = (
    <div className="d-flex align-items-center gap-3">
      <Searcher
        name="searchQuery"
        placeholder="Wpisz numer kwitu..."
        onSearchChange={onSearchChange}
        autoFocus
      />
      {externalContractorId && (
        <BasicSwitch
          name="filterByContractor"
          label="Filtrowanie po kontrahencie"
          value={filterByContractor}
          onChange={handleFilterByContractorChange}
          skipFormMargin
        />
      )}
    </div>
  );

  const table = (
    <ExternalWeighingTicketTable
      items={items}
      selectedItems={selectedItems}
      onSelectedItemsChanged={setSelectedItems}
    />
  );

  useEffect(() => {
    onSearchChange({ });
  }, []);

  return (
    <SelectTable
      table={table}
      label="Wyszukiwarka kwitów wagowych"
      searcher={searcher}
      selectContent={selectContent}
      show={show}
      setShow={handleSetShow}
      btnSize="small"
      btnVariant="primary"
      modalTitle="Wyszukiwarka kwitów wagowych"
    />
  );
}
